import * as React from 'react';

import { collectDomain, toNumericSeries } from '../core/scales';
import type { ChartSeries, ChartValueFormatter } from '../core/types';
import { useChartSize } from '../core/use-chart-size';
import { useChartTooltip } from '../core/use-chart-tooltip';
import { Legend } from '../primitives/Legend';
import { CHART_TOOLTIP_CLASS, ChartTooltipBody } from '../primitives/Tooltip';

import { cn } from '../../utils';

export interface HorizontalBarChartProps {
  series: ChartSeries[];
  /** Minimum chart height in px — grows when rows need more room */
  height?: number;
  formatValue: ChartValueFormatter;
  /** Formats the category label of a row */
  formatCategory?: (value: string | number) => string;
  showLegend?: boolean;
  ariaLabel?: string;
  className?: string;
}

/** Fraction of the width reserved for row labels, capped in px. */
const LABEL_FRACTION = 0.32;
const LABEL_MAX_WIDTH = 160;
const MIN_ROW_HEIGHT = 26;
const BAR_GAP_RATIO = 0.3;
const BAR_RADIUS = 4;

/**
 * Bar outline rounded on the right edge only — mirrors `barPath` for
 * horizontal orientation. Pure so it can be asserted without a DOM.
 */
export function horizontalBarPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
): string {
  // A radius past half the height or the full width cannot be drawn as a corner.
  const r = Math.max(Math.min(radius, height / 2, width), 0);
  if (r === 0) return `M${x},${y} H${x + width} V${y + height} H${x} Z`;

  return (
    `M${x},${y} H${x + width - r} A${r},${r} 0 0 1 ${x + width},${y + r} ` +
    `V${y + height - r} A${r},${r} 0 0 1 ${x + width - r},${y + height} H${x} Z`
  );
}

/** Horizontal bar chart — one row per category, bars grow to the right. Suits rankings. */
export const HorizontalBarChart = ({
  series,
  height = 240,
  formatValue,
  formatCategory,
  showLegend = false,
  ariaLabel,
  className
}: HorizontalBarChartProps) => {
  const { ref, width } = useChartSize();
  const { tooltip, show, hide } = useChartTooltip();

  const domain = React.useMemo(() => collectDomain(series, 'band'), [series]);
  const numeric = React.useMemo(() => toNumericSeries(series, domain), [series, domain]);

  const maxValue = React.useMemo(() => {
    let max = 0;
    for (const s of numeric) {
      for (const value of s.values) {
        if (value !== undefined && value > max) max = value;
      }
    }
    return max;
  }, [numeric]);

  const labelWidth = Math.min(width * LABEL_FRACTION, LABEL_MAX_WIDTH);
  const margin = { top: 4, right: 12, bottom: 4, left: labelWidth };
  const chartHeight = Math.max(height, domain.length * MIN_ROW_HEIGHT + margin.top + margin.bottom);
  const innerWidth = Math.max(width - margin.left - margin.right, 0);
  const innerHeight = Math.max(chartHeight - margin.top - margin.bottom, 0);
  const rowHeight = domain.length > 0 ? innerHeight / domain.length : 0;
  const barAreaHeight = rowHeight * (1 - BAR_GAP_RATIO);
  const barHeight = numeric.length > 0 ? barAreaHeight / numeric.length : 0;

  const xFor = (value: number): number => (maxValue > 0 ? (value / maxValue) * innerWidth : 0);

  const handleHover = (rowIndex: number, event: React.MouseEvent<SVGElement>): void => {
    const bounds = event.currentTarget.closest('svg')?.getBoundingClientRect();
    if (!bounds) return;

    const entries = numeric
      .filter(s => s.values[rowIndex] !== undefined)
      .map(s => ({
        label: s.label,
        value: formatValue(s.values[rowIndex] as number, s.currency),
        color: s.color
      }));
    const raw = domain[rowIndex];
    const title = formatCategory ? formatCategory(raw) : String(raw);
    show({ x: event.clientX - bounds.left, y: event.clientY - bounds.top, title, entries });
  };

  return (
    <div className={className}>
      <div ref={ref} className='relative w-full' style={{ height: chartHeight }}>
        <svg
          width='100%'
          height={chartHeight}
          viewBox={`0 0 ${width} ${chartHeight}`}
          role='img'
          aria-label={ariaLabel}
          data-slot='chart-root'
        >
          <g transform={`translate(${margin.left},${margin.top})`}>
            {domain.map((x, rowIndex) => {
              const rowTop = rowIndex * rowHeight + (rowHeight - barAreaHeight) / 2;
              const raw = formatCategory ? formatCategory(x) : String(x);
              return (
                <g
                  key={String(x)}
                  data-slot='chart-row'
                  onMouseMove={event => handleHover(rowIndex, event)}
                  onMouseLeave={hide}
                >
                  {/* Invisible hover target spanning the full row */}
                  <rect
                    x={-margin.left}
                    y={rowIndex * rowHeight}
                    width={width}
                    height={rowHeight}
                    fill='transparent'
                  />
                  <text
                    x={-8}
                    y={rowIndex * rowHeight + rowHeight / 2}
                    textAnchor='end'
                    dominantBaseline='middle'
                    className='fill-foreground-muted text-xs'
                    data-slot='chart-row-label'
                  >
                    {raw}
                  </text>
                  {numeric.map((s, seriesIndex) => {
                    const value = s.values[rowIndex];
                    if (value === undefined) return null;
                    return (
                      <path
                        key={s.label}
                        data-slot='chart-bar'
                        d={horizontalBarPath(
                          0,
                          rowTop + seriesIndex * barHeight,
                          Math.max(xFor(value), 0),
                          Math.max(barHeight - 1, 0),
                          BAR_RADIUS
                        )}
                        fill={s.color}
                      />
                    );
                  })}
                </g>
              );
            })}
          </g>
        </svg>
        {tooltip && (
          <div
            data-slot='chart-tooltip'
            role='tooltip'
            className={cn(CHART_TOOLTIP_CLASS, '-translate-y-1/2')}
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <ChartTooltipBody title={tooltip.title} entries={tooltip.entries} />
          </div>
        )}
      </div>
      {showLegend && <Legend items={numeric.map(s => ({ label: s.label, color: s.color }))} />}
    </div>
  );
};

HorizontalBarChart.displayName = 'HorizontalBarChart';
